import React, { Fragment, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { TextField, Checkbox, InputAdornment } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Formik, Form, Field } from 'formik';
import { RenderTextField } from '../../../../components/FormElements/FormikElements';
import { SmallToggleSwitch } from '../../../../components/ToggleSwitch/ToggleSwitch';
import StickyButtons from '../../../../components/StickyButtons';
import { Icon } from 'merchant-portal-components';
import OptionOverridesModalContainer from '../OptionOverridesModalContainer';
import { LoadingBlueLarge } from 'merchant-portal-components';
import { SELECT_LOCATION_SCREEN, SUMMARY_SCREEN } from '../priceTypes';
import { finalStructureSummaryData } from '../utils/helper';
import _ from 'lodash';

const Card = styled.div`
  background-color: #fff;
  border-radius: 20px;
  padding-bottom: 48px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.14);
`;
const Title = styled.div`
  margin-bottom: 15px;
  font-size: 17px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000;
`;
const SearchWrapper = styled.div`
  padding: 25px 50px 35px;
`;
const HeaderWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  padding: 48px 51px 9px 51px;
  grid-gap: 10px;
`;
const HorizontalLine = styled.hr`
  border-top: 1px solid #e1e1e1;
`;
const Header = styled.div`
  font-weight: 600;
`;

const RowWrapper = styled.div`
  padding: 24px 32px;
  display: grid;
  grid-gap: 10px;
  border: 1px solid #e1e1e1;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
`;

const VariantName = styled.div`
  color: ${(props) => (props.isSelected ? null : 'red')};
`;

const VariantWrapper = styled.div`
  display: grid;
  grid-gap: 15px;
  align-items: center;
`;

const OverridesWrapper = styled.div`
  text-align: center;
`;

const IconWrapper = styled(Icon)`
  margin-left: 6px;
  font-size: 14px;
`;

const validatePrices = (items, priceKey) => {
  let errors = [];
  items.forEach((item) => {
    if (item.sub_items) {
      item.sub_items.forEach((subItem) => {
        subItem.prices.forEach((price) => {
          if (
            price.isSelected &&
            (price.price === null || price.price === '')
          ) {
            errors = [
              ...errors,
              {
                id: item.id,
                sub_item_id: subItem.id,
                variant_id: price.variant_id,
                error: 'Required',
              },
            ];
          }
          if (
            price.isSelected &&
            (price.additional_price === null || price.additional_price === '')
          ) {
            errors = [
              ...errors,
              {
                id: item.id,
                sub_item_id: subItem.id,
                variant_id: price.variant_id,
                error: 'Required',
              },
            ];
          }
        });
      });
    } else {
      item.prices.forEach((price) => {
        if (price.isSelected && (price.price === null || price.price === '')) {
          errors = [
            ...errors,
            { id: item.id, variant_id: price.variant_id, error: 'Required' },
          ];
        }
        if (
          price.isSelected &&
          (price.additional_price === null || price.additional_price === '')
        ) {
          errors = [
            ...errors,
            { id: item.id, variant_id: price.variant_id, error: 'Required' },
          ];
        }
      });
    }
  });

  return errors;
};

const OptionPricesForm = ({
  items,
  selectedOption,
  optionPrices,
  includedPick,
  handleSave,
  isRequesting,
  itemOverrides,
  setSelectedOption,
  isRequestingItemOverrides,
  formattedBusinesses,
  isOptionOverridesModal,
  setIsOptionOverridesModal,
  page,
  setPage,
  isOptionDisabled,
  setIsOptionDisabled,
}) => {
  const [selectedItemIdList, setSelectedItemIdList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedValue, setSelectedValue] = useState([]);
  const [itemsWithOverrides, setItemsWithOverrides] = useState([]);
  const priceKey = includedPick === 0 ? 'price' : 'additional_price';

  const handleSelectItem = (_, newValue) => {
    const ids = newValue.map((i) => i.id);
    if (ids.includes(0)) {
      if (ids.length <= items.length) {
        setSelectedValue(items);
        setSelectedItemIdList(items.map((i) => i.id));
      } else {
        setSelectedValue([]);
        setSelectedItemIdList([]);
      }
    } else {
      setSelectedValue(items.filter((i) => ids.includes(i.id)));
      setSelectedItemIdList(ids);
    }
  };
  useEffect(() => {
    const withAll = [{ id: 0, name: 'All Option Items' }, ...items];
    setSelectedItems(withAll);
    const existing = new Set(selectedValue.map((i) => i.id));
    const stillValid = items.filter((i) => existing.has(i.id));
    setSelectedValue(stillValid);
    setSelectedItemIdList(stillValid.map((i) => i.id));
  }, [items]);
  useEffect(() => {
    if (!setSelectedOption || !itemOverrides?.length) return;
    const flatOv = itemOverrides.map((o) => o.data);
    let tot = 0;

    const withCounts = items.map((it, idx) => {
      let sub = [];
      if (idx > 0) {
        tot += items[idx - 1].sub_items?.length ?? 1;
      }
      if (it.sub_items) {
        sub = it.sub_items.map((si, siIdx) => {
          const od = flatOv[siIdx + tot] || [];
          if (!od.length) return { ...si, overrides: 0 };
          const summary = finalStructureSummaryData(
            flatOv.find((o) =>
              o.some((x) => x.product_option_item_id === si.id)
            ) || [],
            si,
            formattedBusinesses,
            priceKey
          );
          const count = summary
            .map((d) => ({
              ...d,
              item: {
                prices: d.item.prices.filter((v) => v.name && v.isSelected),
              },
            }))
            .filter((d) => d.item.prices.length > 0).length;
          return { ...si, overrides: count };
        });
        return { ...it, sub_items: sub };
      }
      const od = flatOv[tot] || [];
      if (!od.length) return { ...it, overrides: 0 };
      const summary = finalStructureSummaryData(
        flatOv.find((o) => o.some((x) => x.product_option_item_id === it.id)) ||
          [],
        it,
        formattedBusinesses,
        priceKey
      );
      const count = summary
        .map((d) => ({
          ...d,
          item: {
            prices: d.item.prices.filter((v) => v.name && v.isSelected),
          },
        }))
        .filter((d) => d.item.prices.length > 0).length;
      return { ...it, overrides: count };
    });

    setItemsWithOverrides(withCounts);
  }, [
    items,
    itemOverrides,
    setSelectedOption,
    includedPick,
    formattedBusinesses,
    priceKey,
  ]);

  const submitHandler = (filteredItems) => {
    const ids = new Set(filteredItems.map((i) => i.id));
    const out = items.map((it) =>
      ids.has(it.id) ? filteredItems.find((fi) => fi.id === it.id) : it
    );
    handleSave(out, filteredItems);
  };

  if (isRequestingItemOverrides) return <LoadingBlueLarge />;

  return (
    <div>
      <Card>
        <SearchWrapper>
          <Title>Search or Select Items</Title>
          <Autocomplete
            multiple
            id='checkboxes-tags-demo'
            options={selectedItems}
            disableCloseOnSelect
            getOptionLabel={(item) => item.name}
            onChange={(e, selectedItems) => {
              handleSelectItem(e, selectedItems);
            }}
            value={selectedValue}
            renderOption={(item, option) => (
              <div style={{ fontWeight: item.id === 0 ? 'bold' : 'normal' }}>
                <Checkbox
                  color='primary'
                  style={{
                    marginRight: '5px',
                    transform: 'scale(0.9)',
                  }}
                  checked={
                    selectedItemIdList?.includes(0) ||
                    selectedItemIdList?.length === items?.length
                      ? true
                      : selectedItemIdList?.includes(item.id)
                      ? true
                      : option.selected
                  }
                />
                {item.name}
              </div>
            )}
            style={{ width: '100%' }}
            renderInput={(params) => (
              <TextField {...params} label='Select Items' variant='outlined' />
            )}
            disabled={isOptionDisabled}
          />
        </SearchWrapper>
        {selectedItemIdList.length > 0 && (
          <Formik
            initialValues={{ items: selectedValue }}
            enableReinitialize={true}
          >
            {({ dirty, values, setFieldValue, handleReset }) => (
              <>
                <Form onLoad={() => setIsOptionDisabled(dirty)}>
                  <HeaderWrapper>
                    <Header>Items</Header>
                    <Header>Price Equal Across Variants?</Header>
                    <Header>Variants</Header>
                    <Header>Price</Header>
                    <Header>Additional Price</Header>
                    <Header>Add Overrides</Header>
                  </HeaderWrapper>
                  <HorizontalLine />
                  {values.items.map((item, idx) => {
                    return (
                      <Fragment key={item.id}>
                        <RowWrapper>
                          <div>{item.name}</div>
                          {!item.sub_items && (
                            <Fragment>
                              <div style={{ textAlign: 'center' }}>
                                <SmallToggleSwitch
                                  checked={item.variantsAreEqual}
                                  onChange={(e) => {
                                    setFieldValue(
                                      `items[${idx}].variantsAreEqual`,
                                      e.target.checked
                                    );

                                    item.prices.forEach((price, priceIndex) => {
                                      priceIndex !== 0 &&
                                        setFieldValue(
                                          `items[${idx}].prices[${priceIndex}].price`,
                                          item.prices[0].price
                                        );
                                      priceIndex !== 0 &&
                                        setFieldValue(
                                          `items[${idx}].prices[${priceIndex}].additional_price`,
                                          item.prices[0].additional_price
                                        );
                                    });
                                  }}
                                />
                              </div>
                              <VariantWrapper>
                                {!item.variantsAreEqual ? (
                                  item.prices?.map((price, priceIndex) => (
                                    <VariantName
                                      isSelected={price.isSelected}
                                      key={priceIndex}
                                    >
                                      {price.variantName}
                                    </VariantName>
                                  ))
                                ) : (
                                  <div>
                                    {
                                      item.prices.filter(
                                        (price) => price.isSelected
                                      ).length
                                    }{' '}
                                    Variants
                                  </div>
                                )}
                              </VariantWrapper>
                              <VariantWrapper>
                                {!item.variantsAreEqual ? (
                                  item.prices?.map((price, priceIndex) =>
                                    price.isSelected ? (
                                      <Field
                                        key={priceIndex}
                                        name={`items[${idx}].prices[${priceIndex}].price`}
                                        type='number'
                                        component={RenderTextField}
                                        required
                                        label='Price'
                                        isPrice={true}
                                        isStepperDisabled={true}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position='start'>
                                              $
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    ) : (
                                      <div style={{ height: '55px' }} />
                                    )
                                  )
                                ) : (
                                  <Field
                                    name={`items[${idx}].prices[0].price`}
                                    component={RenderTextField}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position='start'>
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                    required
                                    label='Price'
                                    isPrice={true}
                                    isStepperDisabled={true}
                                    onChange={(e) =>
                                      item.prices.forEach(
                                        (price, priceIndex) => {
                                          priceIndex !== 0 &&
                                            setFieldValue(
                                              `items[${idx}].prices[${priceIndex}].price`,
                                              e.target.value
                                            );
                                        }
                                      )
                                    }
                                  />
                                )}
                              </VariantWrapper>
                              <VariantWrapper>
                                {!item.variantsAreEqual ? (
                                  item.prices?.map((price, priceIndex) =>
                                    price.isSelected ? (
                                      <Field
                                        key={priceIndex}
                                        name={`items[${idx}].prices[${priceIndex}].additional_price`}
                                        component={RenderTextField}
                                        required
                                        label='Additional Price'
                                        isPrice={true}
                                        isStepperDisabled={true}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position='start'>
                                              $
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    ) : (
                                      <div style={{ height: '55px' }} />
                                    )
                                  )
                                ) : (
                                  <Field
                                    name={`items[${idx}].prices[0].additional_price`}
                                    component={RenderTextField}
                                    required
                                    label='Additional Price'
                                    isPrice={true}
                                    isStepperDisabled={true}
                                    onChange={(e) =>
                                      item.prices.forEach(
                                        (price, priceIndex) => {
                                          priceIndex !== 0 &&
                                            setFieldValue(
                                              `items[${idx}].prices[${priceIndex}].additional_price`,
                                              e.target.value
                                            );
                                        }
                                      )
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position='start'>
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              </VariantWrapper>
                              <OverridesWrapper>
                                {(itemsWithOverrides[idx]?.overrides ?? 0) === 0
                                  ? 'No Overrides'
                                  : 'Overrides'}
                                <IconWrapper
                                  className={
                                    itemsWithOverrides[idx]?.overrides === 0
                                      ? 'fal fa-plus'
                                      : 'fas fa-pencil'
                                  }
                                  onClick={() => {
                                    setIsOptionOverridesModal({
                                      isShowing: true,
                                      item,
                                      itemIndex: idx,
                                    });
                                    setPage(
                                      itemsWithOverrides[idx].overrides === 0
                                        ? 'SELECT_LOCATION_SCREEN'
                                        : 'SUMMARY_SCREEN'
                                    );
                                  }}
                                />
                              </OverridesWrapper>
                            </Fragment>
                          )}
                        </RowWrapper>
                        {item.sub_items &&
                          item.sub_items?.map((subItem, subItemIndex) => {
                            const overridesForSubItem =
                              itemOverrides.find((bundle) =>
                                bundle.data.some(
                                  (override) =>
                                    override.product_option_item_id ===
                                    subItem.id
                                )
                              )?.data || [];
                            return (
                              <RowWrapper key={subItemIndex}>
                                <div style={{ paddingLeft: '20px' }}>
                                  {subItem.name}
                                </div>
                                <div>
                                  <SmallToggleSwitch
                                    checked={subItem.variantsAreEqual}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `items[${idx}].sub_items[${subItemIndex}].variantsAreEqual`,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </div>
                                <VariantWrapper>
                                  {!subItem.variantsAreEqual ? (
                                    subItem.prices?.map((price, priceIndex) => (
                                      <VariantName
                                        isSelected={price.isSelected}
                                        key={priceIndex}
                                      >
                                        {price.variantName}
                                      </VariantName>
                                    ))
                                  ) : (
                                    <div>
                                      {
                                        subItem.prices.filter(
                                          (price) => price.isSelected
                                        ).length
                                      }{' '}
                                      Variants
                                    </div>
                                  )}
                                </VariantWrapper>
                                <VariantWrapper>
                                  {!subItem.variantsAreEqual ? (
                                    subItem.prices?.map((price, priceIndex) =>
                                      price.isSelected ? (
                                        <Field
                                          key={priceIndex}
                                          name={`items[${idx}].sub_items[${subItemIndex}].prices[${priceIndex}].price`}
                                          component={RenderTextField}
                                          required
                                          label='Price'
                                          isPrice={true}
                                          isStepperDisabled={true}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                $
                                              </InputAdornment>
                                            ),
                                          }}
                                        />
                                      ) : (
                                        <div style={{ height: '55px' }} />
                                      )
                                    )
                                  ) : (
                                    <Field
                                      name={`items[${idx}].sub_items[${subItemIndex}].prices[0].price`}
                                      component={RenderTextField}
                                      required
                                      label='Price'
                                      isPrice={true}
                                      isStepperDisabled={true}
                                      onChange={(e) =>
                                        subItem.prices.forEach(
                                          (price, priceIndex) => {
                                            priceIndex !== 0 &&
                                              setFieldValue(
                                                `items[${idx}].sub_items[${subItemIndex}].prices[${priceIndex}].price`,
                                                e.target.value
                                              );
                                          }
                                        )
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            $
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                </VariantWrapper>
                                <VariantWrapper>
                                  {!subItem.variantsAreEqual ? (
                                    subItem.prices?.map((price, priceIndex) =>
                                      price.isSelected ? (
                                        <Field
                                          key={priceIndex}
                                          name={`items[${idx}].sub_items[${subItemIndex}].prices[${priceIndex}].additional_price`}
                                          component={RenderTextField}
                                          required
                                          label='Additional Price'
                                          isPrice={true}
                                          isStepperDisabled={true}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                $
                                              </InputAdornment>
                                            ),
                                          }}
                                        />
                                      ) : (
                                        <div style={{ height: '55px' }} />
                                      )
                                    )
                                  ) : (
                                    <Field
                                      name={`items[${idx}].sub_items[${subItemIndex}].prices[0].additional_price`}
                                      component={RenderTextField}
                                      required
                                      label='Additional Price'
                                      isStepperDisabled={true}
                                      isPrice={true}
                                      onChange={(e) =>
                                        subItem.prices.forEach(
                                          (price, priceIndex) => {
                                            priceIndex !== 0 &&
                                              setFieldValue(
                                                `items[${idx}].sub_items[${subItemIndex}].prices[${priceIndex}].additional_price`,
                                                e.target.value
                                              );
                                          }
                                        )
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            $
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                </VariantWrapper>

                                <OverridesWrapper>
                                  {overridesForSubItem.length === 0
                                    ? 'No Overrides'
                                    : 'Overrides'}
                                  <IconWrapper
                                    className={
                                      overridesForSubItem.length === 0
                                        ? 'fal fa-plus'
                                        : 'fas fa-pencil'
                                    }
                                    onClick={() => {
                                      setIsOptionOverridesModal({
                                        isShowing: true,
                                        item: subItem,
                                        // compute the correct itemIndex for your subItem here
                                        itemIndex:
                                          subItemIndex +
                                          /* your totSubItem offset */ idx,
                                      });
                                      setPage(
                                        overridesForSubItem.length === 0
                                          ? SELECT_LOCATION_SCREEN
                                          : SUMMARY_SCREEN
                                      );
                                    }}
                                  />
                                </OverridesWrapper>
                              </RowWrapper>
                            );
                          })}
                        <StickyButtons
                          isVisible={dirty}
                          handleDiscard={() => handleReset()}
                          saveIsDisabled={
                            validatePrices(values.items, priceKey).length ||
                            isRequesting
                          }
                          handleSave={() => {
                            submitHandler(values.items);
                          }}
                          title='Price Management'
                        />
                      </Fragment>
                    );
                  })}
                </Form>
              </>
            )}
          </Formik>
        )}
      </Card>

      {isOptionOverridesModal.isShowing && (
        <OptionOverridesModalContainer
          item={isOptionOverridesModal.item}
          setIsOptionOverridesModal={setIsOptionOverridesModal}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          priceKey={priceKey}
          singleItemOverrides={
            itemOverrides.find((bundle) =>
              bundle.data.some(
                (override) =>
                  override.product_option_item_id ===
                  isOptionOverridesModal.item.id
              )
            )?.data || []
          }
          page={page}
          setPage={setPage}
          items={items}
          formattedBusinesses={formattedBusinesses}
        />
      )}
    </div>
  );
};

export default OptionPricesForm;
